import { documentsKeys } from '~domains/profile/entities/documents';
import {
  type BasicInfoFormValues,
  type CurrentAddressFormValues,
  formatPersonValues,
  type IdDocumentFormValues,
  PersonMachineContext,
  type SelfieFormValues,
  useUpdatePersonData,
} from '~domains/profile/entities/person';

import { ampli } from '~shared/analytics';

import { useQueryClient } from '@tanstack/react-query';

import { BasicInfo } from './screens/basic-info';
import { CurrentAddress } from './screens/current-address';
import { IdDocument } from './screens/id-document';
import { SelfieUpload } from './screens/selfie-upload';
import { StartScreen } from './screens/start-screen';

export const PersonFlowScreens = () => {
  const state = PersonMachineContext.useSelector((snapshot) => snapshot);
  const actor = PersonMachineContext.useActorRef();
  const { basicInfo, idDocument, selfie, currentAddress } =
    PersonMachineContext.useSelector((s) => s.context);
  const updatePersonMutation = useUpdatePersonData();
  const queryClient = useQueryClient();

  const goBack = () => actor.send({ type: 'back' });

  if (state.matches('start')) {
    return (
      <StartScreen
        onSubmit={() => {
          ampli.mpProfileStartScreenNextClick();
          actor.send({ type: 'next' });
        }}
      />
    );
  }

  if (state.matches('basicInfo')) {
    return (
      <BasicInfo
        initialValues={basicInfo}
        onSubmit={async (values: BasicInfoFormValues) => {
          ampli.mpProfileBasicInfoScreenNextClick();
          actor.send({ type: 'basicInfo.submitData', values });
          await updatePersonMutation.mutateAsync(
            formatPersonValues(actor.getSnapshot().context)
          );
          actor.send({ type: 'next' });
        }}
      />
    );
  }

  if (state.matches('currentAddress')) {
    return (
      <CurrentAddress
        initialValues={currentAddress}
        onBack={() => {
          ampli.mpProfileAddressScreenBackClick();
          goBack();
        }}
        onSubmit={async (values: CurrentAddressFormValues) => {
          ampli.mpProfileAddressScreenNextClick();
          actor.send({ type: 'currentAddress.submitData', values });
          await updatePersonMutation.mutateAsync(
            formatPersonValues(actor.getSnapshot().context)
          );
          actor.send({ type: 'next' });
        }}
      />
    );
  }

  if (state.matches('idDocument')) {
    return (
      <IdDocument
        initialValues={idDocument}
        onBack={() => {
          ampli.mpProfileUploadIdScreenBackClick();
          goBack();
        }}
        onSubmit={(values: IdDocumentFormValues) => {
          ampli.mpProfileUploadIdScreenNextClick();
          actor.send({ type: 'idDocument.submitData', values });
          actor.send({ type: 'next' });
        }}
      />
    );
  }

  if (state.matches('selfie')) {
    return (
      <SelfieUpload
        initialValues={selfie}
        onBack={() => {
          ampli.mpProfileSelfieScreenBackClick();
          goBack();
        }}
        onSubmit={async (values: SelfieFormValues) => {
          ampli.mpProfileSelfieScreenNextClick();
          actor.send({ type: 'selfie.submitData', values });
          await queryClient.invalidateQueries({
            queryKey: documentsKeys.checkPartnerSelfie(),
          });
          actor.send({ type: 'next' });
        }}
      />
    );
  }

  return null;
};
